/**
 * @file Renders the CSV template download and upload controls for the transfer import step.
 */
import { useState } from "react";
import { Space, Button, Upload } from "antd";
import type { UploadProps } from "antd";
import { DownloadOutlined, UploadOutlined, CloseOutlined } from "@ant-design/icons";

import { getCustomRequest } from "@components/Import";

import useExport from "./helpers/useExport";
import useTemplateParser from "./helpers/useTemplateParser";
import type { SetState, TableRef, Investment, ImportError, CustomAmounts } from "./types";

const LABEL_UPLOAD_TEMPLATE = "Select Template";
const LABEL_DOWNLOAD_TEMPLATE = "CSV Template";

type Props = {
  table: TableRef;
  setErrors: SetState<ImportError[]>;
  sourceProjectId: string;
  targetProjectId: string;
  setCustomAmounts: SetState<CustomAmounts[]>;
  sourceInvestments: Investment[];
};

/**
 * Renders the download-template button and the CSV upload control used to import custom transfer amounts.
 */
const Import = ({
  table,
  setErrors,
  sourceProjectId,
  targetProjectId,
  setCustomAmounts,
  sourceInvestments,
}: Props) => {
  const [ file, setFile ] = useState<File | undefined>();

  /**
   * Returns the source investments still selected for transfer.
   */
  const getItems = () => table.current.getDataSource().filter(({ isIncluded }) => isIncluded);

  const onExportClick = useExport({
    getItems,
    sourceProjectId,
    targetProjectId,
    sourceInvestments,
  });

  const { parseRow, validateRow, buildRow } = useTemplateParser(setErrors, getItems, targetProjectId);

  /**
   * Stores the uploaded file so its name can show next to the reset control.
   */
  const callbackUpload = (selectedFile: File) => setFile(selectedFile);
  /**
   * Stores the custom amounts parsed from the uploaded CSV.
   */
  const callbackSuccess = (value: CustomAmounts[]) => setCustomAmounts(value);

  const uploadRequest = getCustomRequest(parseRow, validateRow, buildRow, callbackSuccess, callbackUpload);

  /**
   * Runs the CSV upload request through the shared import handler.
   */
  const customRequest: UploadProps["customRequest"] = ({ file: uploadedFile, onSuccess }) => {
    uploadRequest({ file: uploadedFile, onSuccess });
  };

  /**
   * Clears the uploaded file, the import errors and the parsed custom amounts.
   */
  const onReset = () => {
    setFile(undefined);
    setErrors([]);
    setCustomAmounts([]);
  };

  return (
    <Space>
      <Button
        icon={<DownloadOutlined />}
        onClick={onExportClick}
      >
        {LABEL_DOWNLOAD_TEMPLATE}
      </Button>

      {
        !file && (
          <Upload
            accept=".csv"
            multiple={false}
            customRequest={customRequest}
            showUploadList={false}
          >
            <Button
              type="primary"
              icon={<UploadOutlined />}
            >
              {LABEL_UPLOAD_TEMPLATE}
            </Button>
          </Upload>
        )
      }

      {
        file && (
          <span>
            {file.name}
          </span>
        )
      }

      {
        file && (
          <Button
            icon={<CloseOutlined />}
            size="small"
            type="text"
            shape="circle"
            onClick={onReset}
          />
        )
      }
    </Space>
  );
};

export default Import;
